import type { SupabaseClient } from '@supabase/supabase-js';
import { ticketDocumentExtension } from '@/lib/tickets/questions';

/**
 * Uploads a ticket-question document straight to the private bucket (never
 * through an API route – Vercel refuses bodies over 4.5 MB there) and returns
 * its storage path. Throws Error(message) when the bucket refuses the file.
 */
export async function uploadTicketDocument(
  supabase: SupabaseClient,
  input: { userId: string; ticketId: string; questionId: string; file: File },
): Promise<string> {
  const { userId, ticketId, questionId, file } = input;

  // The readback route only signs plainly well-formed keys
  // (isWellFormedTicketDocumentPath), so the extension has to be forced
  // into that character set here - a real upload named "scan.pdf " or
  // "паспорт.документ" would otherwise produce a key the signing route has
  // to refuse, 403ing the participant's own document. A name that carries no
  // usable extension falls back to the file's mime type rather than to a
  // meaningless ".bin" - see ticketDocumentExtension.
  const fileName = `${crypto.randomUUID()}.${ticketDocumentExtension(file.name, file.type)}`;
  // First segment must be the user id - the storage policy matches on it.
  const filePath = `${userId}/${ticketId}/${questionId}/${fileName}`;

  const { error } = await supabase.storage.from('ticket-documents').upload(filePath, file);
  if (error) throw new Error(error.message);

  // Private bucket: store the path, not a public URL. Readback is signed on demand.
  return filePath;
}
